'use client';

import React, { useMemo, useState, useCallback } from 'react';
import { useParams } from 'next/navigation';
import { Dialog, Button, Typography } from 'src/components';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import useRoles from 'src/hooks/roles/useRoles';
import { useGetCompanyRolesQuery } from 'src/api/redux';
import { deleteRole } from 'src/api';

interface DeleteRoleDialogProps {
  open: boolean;
  onClose: () => void;
  roleId: string;
}

const DeleteRoleDialog = ({ open, onClose, roleId }: DeleteRoleDialogProps) => {
  const { companyId } = useParams<{ companyId: string }>();
  const log = useLogging();
  const { setNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);

  const { selectedRole, setSelectedRole } = useRoles();
  const { data: roles, refetch } = useGetCompanyRolesQuery(
    { companyId },
    { skip: !companyId }
  );

  const handleResetRole = useCallback(() => {
    if (selectedRole?.id === roleId) {
      setSelectedRole(null);
    }
  }, [selectedRole?.id, roleId, setSelectedRole]);

  const role = useMemo(() => {
    if (!roles) return null;
    return roles?.results.find((role) => role.id === roleId);
  }, [roles, roleId]);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleDeleteRole = useCallback(async () => {
    setIsLoading(true);
    try {
      await deleteRole({ companyId, roleId });
      onClose();
    } catch (err) {
      log.exception(`Error deleting role ${roleId}`, err);
      setNotification({
        title: 'Error',
        message: err.error_message,
        type: 'error',
      });
    } finally {
      refetch();
      setIsLoading(false);
      handleResetRole();
      onClose();
      handleResetRole();
    }
  }, [
    companyId,
    log,
    refetch,
    onClose,
    roleId,
    setNotification,
    handleResetRole,
  ]);

  return (
    <Dialog isOpen={open} onClose={onClose}>
      <Dialog.Header>
        <div className="flex items-center gap-2">
          <Typography variant="h4" className="text-primary">
            Delete Role
          </Typography>
        </div>
      </Dialog.Header>
      <Dialog.Content>
        <div className="flex items-center gap-2 p-4">
          <Typography className="text-neutral-600 text-base font-medium">
            {`Are you sure you want to delete the role "${role?.label}"? This action cannot be undone.`}
          </Typography>
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <div className="flex items-center gap-2 w-full">
          <Button
            variant="outlined"
            color="neutral"
            onClick={handleClose}
            className="w-full rounded-full"
            data-testid="delete-role-dialog-cancel-button"
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            color="danger"
            onClick={handleDeleteRole}
            className="w-full rounded-full"
            loading={isLoading}
            data-testid="delete-role-dialog-delete-button"
          >
            Delete
          </Button>
        </div>
      </Dialog.Actions>
    </Dialog>
  );
};

export default DeleteRoleDialog;
